import type {
  CpopoverAlign,
  CpopoverPlacement,
  PopoverPosition,
} from './Cpopover.types';
import { POPOVER_ALIGN, POPOVER_PLACEMENT } from './constants';

interface ComputePopoverPositionInput {
  anchorRect: DOMRect;
  contentHeight: number;
  placement: CpopoverPlacement;
  align: CpopoverAlign;
  gap: number;
  edgeMargin: number;
  maxWidth: number;
}

interface PopoverViewport {
  width: number;
  height: number;
}

interface PopoverSpaces {
  above: number;
  below: number;
}

interface ResolveDesiredLeftInput {
  align: CpopoverAlign;
  anchorRect: DOMRect;
  width: number;
}

type DesiredLeftResolver = (input: ResolveDesiredLeftInput) => number;

const DESIRED_LEFT_BY_ALIGN: Record<CpopoverAlign, DesiredLeftResolver> = {
  [POPOVER_ALIGN.Center]: ({ anchorRect, width }) =>
    anchorRect.left + (anchorRect.width - width) / 2,
  [POPOVER_ALIGN.End]: ({ anchorRect, width }) => anchorRect.right - width,
  [POPOVER_ALIGN.Start]: ({ anchorRect }) => anchorRect.left,
};

function getPopoverViewport(): PopoverViewport {
  return {
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight,
  };
}

function clamp(input: { value: number; min: number; max: number }): number {
  return Math.max(input.min, Math.min(input.value, input.max));
}

function resolvePopoverWidth(input: {
  edgeMargin: number;
  maxWidth: number;
  viewportWidth: number;
}): number {
  const availableWidth = Math.max(0, input.viewportWidth - input.edgeMargin * 2);
  return Math.min(input.maxWidth, availableWidth);
}

function resolvePopoverSpaces(input: {
  anchorRect: DOMRect;
  edgeMargin: number;
  gap: number;
  viewportHeight: number;
}): PopoverSpaces {
  return {
    below: input.viewportHeight - input.anchorRect.bottom - input.gap - input.edgeMargin,
    above: input.anchorRect.top - input.gap - input.edgeMargin,
  };
}

function resolveDesiredLeft(input: ResolveDesiredLeftInput): number {
  return DESIRED_LEFT_BY_ALIGN[input.align](input);
}

function resolveOpenAbove(input: {
  contentHeight: number;
  placement: CpopoverPlacement;
  spaces: PopoverSpaces;
}): boolean {
  const autoShouldFlip =
    input.placement === POPOVER_PLACEMENT.Auto &&
    input.spaces.below < input.contentHeight &&
    input.spaces.above > input.spaces.below;

  return input.placement === POPOVER_PLACEMENT.Above || autoShouldFlip;
}

function buildAbovePosition(input: {
  align: CpopoverAlign;
  anchorRect: DOMRect;
  gap: number;
  left: number;
  spaces: PopoverSpaces;
  viewportHeight: number;
  width: number;
}): PopoverPosition {
  return {
    left: input.left,
    top: null,
    bottom: input.viewportHeight - input.anchorRect.top + input.gap,
    width: input.width,
    maxHeight: Math.max(0, input.spaces.above),
    openAbove: true,
    align: input.align,
  };
}

function buildBelowPosition(input: {
  align: CpopoverAlign;
  anchorRect: DOMRect;
  gap: number;
  left: number;
  spaces: PopoverSpaces;
  width: number;
}): PopoverPosition {
  return {
    left: input.left,
    top: input.anchorRect.bottom + input.gap,
    bottom: null,
    width: input.width,
    maxHeight: Math.max(0, input.spaces.below),
    openAbove: false,
    align: input.align,
  };
}

export function computePopoverPosition(
  input: ComputePopoverPositionInput,
): PopoverPosition {
  const viewport = getPopoverViewport();
  const width = resolvePopoverWidth({
    edgeMargin: input.edgeMargin,
    maxWidth: input.maxWidth,
    viewportWidth: viewport.width,
  });
  const left = clamp({
    value: resolveDesiredLeft({
      align: input.align,
      anchorRect: input.anchorRect,
      width,
    }),
    min: input.edgeMargin,
    max: viewport.width - width - input.edgeMargin,
  });
  const spaces = resolvePopoverSpaces({
    anchorRect: input.anchorRect,
    edgeMargin: input.edgeMargin,
    gap: input.gap,
    viewportHeight: viewport.height,
  });

  if (
    resolveOpenAbove({
      contentHeight: input.contentHeight,
      placement: input.placement,
      spaces,
    })
  ) {
    return buildAbovePosition({
      align: input.align,
      anchorRect: input.anchorRect,
      gap: input.gap,
      left,
      spaces,
      viewportHeight: viewport.height,
      width,
    });
  }

  return buildBelowPosition({
    align: input.align,
    anchorRect: input.anchorRect,
    gap: input.gap,
    left,
    spaces,
    width,
  });
}

export function arePopoverPositionsEqual(input: {
  previous: PopoverPosition | null;
  next: PopoverPosition;
}): boolean {
  if (!input.previous) {
    return false;
  }

  const previousSignature = getPopoverPositionSignature({ position: input.previous });
  const nextSignature = getPopoverPositionSignature({ position: input.next });

  return previousSignature.every((value, index) =>
    Object.is(value, nextSignature[index]),
  );
}

function getPopoverPositionSignature(input: {
  position: PopoverPosition;
}): ReadonlyArray<number | boolean | string | null> {
  return [
    input.position.left,
    input.position.top,
    input.position.bottom,
    input.position.width,
    input.position.maxHeight,
    input.position.openAbove,
    input.position.align,
  ];
}
